/**
 * lighting.js — the hub's lighting baseline.
 *
 * Three lights total, which is what a 72 Hz mobile GPU budget affords
 * (GDD 12.2): a hemisphere fill for sky/ground bounce, one shadow-casting
 * directional sun, and a warm unshadowed fill from the opposite side so the
 * booths' shadow sides do not go black.
 *
 * The shadow camera is deliberately tight around the playable midway. A wide
 * shadow frustum on this scene scale produces mush.
 *
 * @module world/lighting
 */

import * as THREE from 'three';
import { HUB, PALETTE } from '../constants.js';

/**
 * @param {THREE.Scene} scene
 * @param {{ shadows?: boolean, intensity?: number }} [opts]
 * @returns {{ hemi: THREE.HemisphereLight, sun: THREE.DirectionalLight,
 *             fill: THREE.DirectionalLight }}
 */
export function createLighting(scene, opts = {}) {
  const intensity = opts.intensity ?? 1;

  // Strong enough that shadowed ground keeps its colour instead of going black.
  const hemi = new THREE.HemisphereLight(PALETTE.skyBottom, PALETTE.ground, 1.25 * intensity);
  hemi.position.set(0, 30, 0);
  hemi.name = 'hemi-light';
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xfff0d8, 1.55 * intensity);
  sun.position.set(-26, 32, 18);
  sun.name = 'sun';

  if (opts.shadows !== false) {
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const cam = sun.shadow.camera;
    // Must enclose the ENTIRE walkable area. Ground outside the shadow
    // frustum samples the clamped edge texel and renders as a hard-edged black
    // band — which is exactly what a too-tight frustum looked like here.
    cam.left = -46;
    cam.right = 46;
    cam.top = 52;
    cam.bottom = -52;
    cam.near = 1;
    cam.far = 160;
    sun.shadow.bias = -0.0009;
    sun.shadow.normalBias = 0.022;
    // Aim down the midway rather than at the world origin.
    sun.target.position.set(0, 0, -HUB.slotSpacing * 2.5);
    scene.add(sun.target);
  }
  scene.add(sun);

  const fill = new THREE.DirectionalLight(PALETTE.lampWarm, 0.42 * intensity);
  fill.position.set(20, 12, -30);
  fill.name = 'fill';
  scene.add(fill);

  return { hemi, sun, fill };
}

/**
 * A warm point light for a stall interior. Kept small in range so several can
 * coexist without blowing the light budget.
 * @param {number} [color]
 * @param {number} [intensity]
 * @param {number} [distance]
 * @returns {THREE.PointLight}
 */
export function createStallLight(color = PALETTE.lampWarm, intensity = 1.4, distance = 9) {
  const light = new THREE.PointLight(color, intensity, distance, 2);
  light.name = 'stall-light';
  return light;
}
